import qs from 'query-string';
import { withWindow } from './withWindow';

export const isValidUrl = (url: Maybe<string>): boolean => {
  if (typeof url !== 'string') {
    return false;
  }
  const res = url.match(/(http(s)?:\/\/.)/g);
  return res !== null;
};

export const getDomainNameFromProductExternalUrl = (productExternalUrl: Maybe<string>) => {
  if (!productExternalUrl || !isValidUrl(productExternalUrl)) {
    return undefined;
  }
  const domain = new URL(productExternalUrl || '');
  const siteName = domain.hostname.replace('www.', '');
  return siteName.charAt(0).toUpperCase() + siteName.slice(1);
};

interface KeyValue {
  [key: string]: string | undefined;
}

// When IE 11 support ends URLSearchParams can be used
export const getURLParams = <ParamsKeyValues extends KeyValue>(search: string): ParamsKeyValues | KeyValue => {
  const paramValue = search
    .substring(1)
    .split('&')
    .reduce((params: KeyValue, newString) => {
      const kv = newString.split('=');
      if (kv[0] && kv[1]) {
        params[kv[0].replace(/\s+/g, '')] = kv[1].trim();
      }
      return params;
    }, {});
  return paramValue;
};

/*
  A function that extract URL from given string. If there is no matching URL, then it will return the given string itself.
  @param {string} text - The text from which the URL to be extracted
  @returns {string} - The extracted URL or the given text itself if no valid URL found
*/
export const extractURLFromText = (text: string) => {
  const urlRegex = /(?:https?:\/\/)?[\w.-]+\.[a-zA-Z]{2,}(\/\S*)?/g;
  const matches = text.match(urlRegex);
  return matches ? matches[0] : text;
};

/*
  To normalize url and make it valid url
*/
export const ensureHttpsPrefix = (url: string) => {
  if (!['http://', 'https://'].some(prefix => url.startsWith(prefix))) {
    return `https://${url}`;
  }

  return url;
};

/**
 * Merges provided query parameters into a target URL, preserving existing parameters
 * and the fragment identifier. New query parameters override existing ones with the same key.
 *
 * @template Params - A map of query param names to their inferred types.
 *
 * @param {string} targetUrl - The URL to merge query parameters into.
 * @param {Partial<Params>} queryParams - Parsed query parameters to merge.
 *   Values can be strings, numbers, or arrays depending on accessor used.
 *
 * @returns {string} The final URL with merged query parameters and any original hash.
 *
 */
export const mergeQueryParamsToUrl = <Params extends Record<string, string | number | string[]>>(targetUrl: string, queryParams: Partial<Params>): string => {
  const {
    url: baseUrl,
    query: targetQuery,
    fragmentIdentifier,
  } = qs.parseUrl(targetUrl, {
    parseFragmentIdentifier: true,
    arrayFormat: 'comma',
  });

  const mergedQuery = { ...targetQuery, ...queryParams };

  return qs.stringifyUrl(
    {
      url: baseUrl,
      query: mergedQuery,
      fragmentIdentifier,
    },
    {
      skipEmptyString: true,
      skipNull: true,
      arrayFormat: 'comma',
    },
  );
};

/**
 * Gets the absolute URL from a pathname.
 *
 * @param pathname - The pathname to get the absolute URL from.
 * @returns The absolute URL.
 *
 * @example
 * getAbsoluteUrlFromPathname("/settings");
 * // => "https://current-domain.com/settings"
 *
 * @example
 * getAbsoluteUrlFromPathname("/");
 * // => "https://current-domain.com/"
 *
 * @example
 * getAbsoluteUrlFromPathname("/docs/page?id=10");
 * // => "https://current-domain.com/docs/page?id=10"
 */
export const getAbsoluteUrlFromPathname = (pathname: string) => {
  return withWindow(global => `${global.location.origin}${pathname}`, pathname);
};
